import React, { useState } from "react";
import { useShow, useCustomMutation } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import {
  Card,
  Descriptions,
  Row,
  Col,
  Table,
  Tag,
  Space,
  Button,
  Modal,
  Input,
  message,
  Typography,
} from "antd";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  INVOICE_BY_ID_QUERY,
  APPROVE_INVOICE_MUTATION,
  REJECT_INVOICE_MUTATION,
} from "./queries";
import { MatchStatus, InvoiceStatus } from "./interface";
import { SubmitButton } from "./components/SubmitButton";

const MatchStatusBadge: React.FC<{ status: MatchStatus }> = ({ status }) => {
  const colorMap: Record<MatchStatus, string> = {
    [MatchStatus.PENDING]: "default",
    [MatchStatus.MATCHED]: "success",
    [MatchStatus.MISMATCH]: "error",
  };
  return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
};

const InvoiceStatusBadge: React.FC<{ status: InvoiceStatus }> = ({ status }) => {
  const colorMap: Record<InvoiceStatus, string> = {
    [InvoiceStatus.PENDING]: "processing",
    [InvoiceStatus.MATCHED]: "blue",
    [InvoiceStatus.SUBMITTED]: "orange",
    [InvoiceStatus.APPROVED]: "success",
    [InvoiceStatus.PAID]: "success",
    [InvoiceStatus.REJECTED]: "error",
  };
  return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
};

export const InvoiceShow: React.FC = () => {
  const [isApprovalOpen, setIsApprovalOpen] = useState<"approve" | "reject" | null>(null);
  const [approvalRemarks, setApprovalRemarks] = useState<string>("");

  const { query } = useShow({
    resource: "invoices",
    meta: {
      gqlQuery: INVOICE_BY_ID_QUERY,
    },
  });

  const { data, isLoading } = query;
  const record = data?.data;

  const { mutate: approveMutate, isLoading: approving } = useCustomMutation();
  const { mutate: rejectMutate, isLoading: rejecting } = useCustomMutation();

  const getApiBaseUrl = () => {
    return import.meta.env.VITE_API_URL 
  };

  const handleViewFile = () => {
    if (record?.fileId) {
      window.open(`${getApiBaseUrl()}/file-storage/preview/${record.fileId}`, '_blank');
    } else {
      message.warning('No invoice file available');
    }
  };

  const handleDownloadFile = () => {
    if (record?.fileId) {
      const link = document.createElement('a');
      link.href = `${getApiBaseUrl()}/file-storage/download/${record.fileId}`;
      link.download = `invoice-${record.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      message.warning('No invoice file available');
    }
  };

  const handleApproveReject = async () => {
    if (!record?.id || !isApprovalOpen) return;
    const meta = isApprovalOpen === "approve" ? { gqlMutation: APPROVE_INVOICE_MUTATION } : { gqlMutation: REJECT_INVOICE_MUTATION };
    const mutate = isApprovalOpen === "approve" ? approveMutate : rejectMutate;
    mutate(
      {
        url: "",
        method: "post",
        values: {
          id: record._id,
          remarks: approvalRemarks || undefined,
        },
        meta,
      },
      {
        onSuccess: () => {
          message.success(isApprovalOpen === "approve" ? "Invoice approved" : "Invoice rejected");
          setIsApprovalOpen(null);
          setApprovalRemarks("");
          query.refetch();
        },
        onError: (e) => {
          console.error(e);
          message.error("Action failed");
        },
      },
    );
  };

  const itemColumns = [
    { title: "PO Line ID", dataIndex: "poLineId" },
    { title: "Description", dataIndex: "description" },
    { title: "Accepted Qty", dataIndex: "acceptedQty" },
    { title: "Invoiced Qty", dataIndex: "invoicedQty" },
    {
      title: "PO Unit Price",
      dataIndex: "poUnitPrice",
      render: (v: number) => `${record?.currency || "ETB"} ${Number(v || 0).toLocaleString()}`,
    },
    {
      title: "Invoice Unit Price",
      dataIndex: "invoiceUnitPrice",
      render: (v: number) => `${record?.currency || "ETB"} ${Number(v || 0).toLocaleString()}`,
    },
    {
      title: "Match Status",
      dataIndex: "matchStatus",
      render: (status: MatchStatus) => <MatchStatusBadge status={status} />,
    },
    { title: "Remarks", dataIndex: "remarks" },
  ];

  const canSubmit = record?.status === InvoiceStatus.PENDING && !record?.submittedBy;
  const canApprove = record?.status === InvoiceStatus.PENDING || record?.status === InvoiceStatus.MATCHED;

  const showApproveReject = canApprove && !(record?.externalWorkflow && record?.approvalType === "EXTERNAL");
  const showSubmit = (canSubmit || (record?.externalWorkflow && record?.approvalType === "EXTERNAL")) && record?.status === InvoiceStatus.MATCHED;

  const handleSubmitSuccess = () => {
    // Refresh the data after successful submission
    query.refetch();
  };

  return (
    <Show
      isLoading={isLoading}
      title={record ? `Invoice • ${record.invoiceNumber}` : "Invoice"}
      headerButtons={
        record && (
          <Space>
            {record.fileId && (
              <>
                <Button icon={<EyeOutlined />} onClick={handleViewFile}>
                  View Invoice
                </Button>
                <Button icon={<DownloadOutlined />} onClick={handleDownloadFile}>
                  Download
                </Button>
              </>
            )}
            {showSubmit && (
              <SubmitButton
                invoiceId={record.id as string}
                invoice={record}
                fiscalYear={new Date().getFullYear()}
                buttonText="Submit"
                onSuccess={handleSubmitSuccess}
              />
            )}
            {showApproveReject && (
              <>
                <Button type="primary" onClick={() => setIsApprovalOpen("approve")} loading={approving}>
                  Approve
                </Button>
                <Button danger onClick={() => setIsApprovalOpen("reject")} loading={rejecting}>
                  Reject
                </Button>
              </>
            )}
          </Space>
        )
      }
    >
      {record && (
        <>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card>
                <Descriptions column={2} layout="vertical" bordered>
                  <Descriptions.Item label="Invoice Number">{record.invoiceNumber}</Descriptions.Item>
                  <Descriptions.Item label="Supplier">{record.supplierName}</Descriptions.Item>
                  <Descriptions.Item label="PO Number">{record.poNumber}</Descriptions.Item>
                  <Descriptions.Item label="Currency">{record.currency}</Descriptions.Item>
                  <Descriptions.Item label="Subtotal">{record.currency} {Number(record.subtotal || 0).toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Tax">{record.currency} {Number(record.tax || 0).toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Total Amount">{record.currency} {Number(record.totalAmount || 0).toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Match Status"><MatchStatusBadge status={record.matchStatus} /></Descriptions.Item>
                  <Descriptions.Item label="Status"><InvoiceStatusBadge status={record.status} /></Descriptions.Item>
                  <Descriptions.Item label="Delivery Refs">{(record.deliveryRefs || []).join(", ") || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Invoice File">
                    {record.fileId ? (
                      <Space>
                        <Typography.Link onClick={handleViewFile}>
                          <EyeOutlined /> View File
                        </Typography.Link>
                        <Typography.Link onClick={handleDownloadFile}>
                          <DownloadOutlined /> Download
                        </Typography.Link>
                      </Space>
                    ) : (
                      <Typography.Text type="secondary">No file attached</Typography.Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">{record.createdAt ? dayjs(record.createdAt).format("YYYY-MM-DD HH:mm") : ""}</Descriptions.Item>
                  <Descriptions.Item label="Updated At">{record.updatedAt ? dayjs(record.updatedAt).format("YYYY-MM-DD HH:mm") : ""}</Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            {record.paymentRequestId && (
              <Col span={24}>
                <Card title="Payment Request">
                  <Descriptions column={2} bordered>
                    <Descriptions.Item label="Payment Request ID">
                      <Typography.Link href={`/payment-requests/show/${record.paymentRequestId}`}>
                        {record.paymentRequestId}
                      </Typography.Link>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            )}

            <Col span={24}>
              <Card title="Invoice Items">
                <Table dataSource={record.items} columns={itemColumns as any} rowKey={(r: any) => r.poLineId} pagination={false}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={6}><strong>Total Items: {record.items?.length || 0}</strong></Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              </Card>
            </Col>
          </Row>

          <Modal
            title={isApprovalOpen === "approve" ? "Approve Invoice" : "Reject Invoice"}
            open={!!isApprovalOpen}
            onOk={handleApproveReject}
            onCancel={() => setIsApprovalOpen(null)}
            okText={isApprovalOpen === "approve" ? "Approve" : "Reject"}
            okButtonProps={{ danger: isApprovalOpen === "reject" }}
            confirmLoading={approving || rejecting}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <div>Remarks</div>
              <Input.TextArea rows={3} value={approvalRemarks} onChange={(e) => setApprovalRemarks(e.target.value)} />
            </Space>
          </Modal>
        </>
      )}
    </Show>
  );
};